import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { cn } from '../../lib/utils';
import { Loader2, User, Check, Copy, Eye, EyeOff, RefreshCw, Building2, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const ORG_ROLES = [
  { value: 'owner', label: 'Owner', description: 'Full control, billing, can delete org' },
  { value: 'product_manager', label: 'Product Manager', description: 'Full product access, can manage team' },
  { value: 'billing_admin', label: 'Billing Admin', description: 'Manage subscription and invoices' },
  { value: 'collaborator', label: 'Collaborator', description: 'View and contribute, limited admin' },
];

const CreateUserModal = ({ isOpen, onClose, onSuccess, preselectedOrg = null }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  
  // Organization selection
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  
  // User data
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [orgRole, setOrgRole] = useState('collaborator');
  const [authMethod, setAuthMethod] = useState('password');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [requirePasswordChange, setRequirePasswordChange] = useState(true);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  
  // Fetch organizations for selection
  useEffect(() => {
    if (isOpen && !preselectedOrg) {
      fetchOrganizations();
    }
    if (preselectedOrg) {
      setSelectedOrg(preselectedOrg);
    }
  }, [isOpen, preselectedOrg]);
  
  const fetchOrganizations = async (search = '') => {
    setLoadingOrgs(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (search) params.append('search', search);
      const res = await api.get(`/api/admin/metrics/organizations?${params}`);
      setOrganizations(res.data.organizations || []);
    } catch (err) {
      toast.error('Failed to load organizations');
    } finally {
      setLoadingOrgs(false);
    }
  };
  
  // Generate random password
  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*';
    let newPassword = '';
    for (let i = 0; i < 12; i++) {
      newPassword += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(newPassword);
    setShowPassword(true);
  };
  
  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };
  
  const copyAllCredentials = () => {
    if (result?.credentials) {
      const creds = result.credentials;
      let text = `Login URL: ${creds.login_url}\nEmail: ${creds.email}`;
      if (creds.password) {
        text += `\nPassword: ${creds.password}`;
      } else if (creds.auth_method === 'google') {
        text += `\nAuth: Sign in with Google`;
      }
      navigator.clipboard.writeText(text);
      toast.success('All credentials copied to clipboard');
    }
  };
  
  // Reset form
  const resetForm = () => {
    setResult(null);
    setSelectedOrg(preselectedOrg || null);
    setOrgSearch('');
    setUserName('');
    setUserEmail('');
    setOrgRole('collaborator');
    setAuthMethod('password');
    setPassword('');
    setShowPassword(false);
    setRequirePasswordChange(true);
    setSendWelcomeEmail(true);
  };
  
  const handleClose = () => {
    resetForm();
    onClose();
  };
  
  // Validation
  const isFormValid = () => {
    if (!selectedOrg) return false;
    if (!userName.trim() || userName.trim().length < 2) return false;
    if (!userEmail.trim() || !userEmail.includes('@')) return false;
    if (authMethod === 'password' && password && password.length < 8) return false;
    return true;
  };
  
  // Submit
  const handleSubmit = async () => {
    if (!isFormValid()) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await api.post('/api/admin/metrics/users/create', {
        organization_id: selectedOrg.organization_id,
        name: userName.trim(),
        email: userEmail.trim().toLowerCase(),
        role: orgRole,
        auth_method: authMethod,
        password: authMethod === 'password' ? (password || null) : null,
        require_password_change: requirePasswordChange,
        send_welcome_email: sendWelcomeEmail
      });
      
      setResult(response.data);
      toast.success('User created successfully');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleOrgSearch = (value) => {
    setOrgSearch(value);
    if (value.length >= 2) {
      fetchOrganizations(value);
    } else if (value.length === 0) {
      fetchOrganizations();
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={cn("max-w-lg max-h-[85vh] overflow-y-auto", colors.bgCard)}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {result ? (
              <>
                <Check className="w-5 h-5 text-green-500" />
                User Created
              </>
            ) : (
              <>
                <User className="w-5 h-5" />
                Create User
              </>
            )}
          </DialogTitle>
          {!result && (
            <DialogDescription>
              Add a new user to an existing organization
            </DialogDescription>
          )}
        </DialogHeader>
        
        {/* Form View */}
        {!result && (
          <div className="space-y-4">
            {/* Organization Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Organization *
              </Label>
              
              {preselectedOrg ? (
                <div className={cn("p-3 rounded-lg border", colors.border, colors.bgSecondary)}>
                  <p className={cn("font-medium", colors.textPrimary)}>{preselectedOrg.name}</p>
                  <p className={cn("text-xs", colors.textMuted)}>{preselectedOrg.organization_id}</p>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
                    <Input
                      value={orgSearch}
                      onChange={(e) => handleOrgSearch(e.target.value)}
                      placeholder="Search organizations..."
                      className="pl-9"
                    />
                  </div>
                  
                  {loadingOrgs ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                  ) : (
                    <div className={cn("max-h-[150px] overflow-y-auto border rounded-lg divide-y", colors.border)}>
                      {organizations.length === 0 ? (
                        <p className={cn("p-3 text-sm text-center", colors.textMuted)}>No organizations found</p>
                      ) : (
                        organizations.map(org => (
                          <button
                            key={org.organization_id}
                            type="button"
                            onClick={() => setSelectedOrg(org)}
                            className={cn(
                              "w-full text-left p-3 hover:bg-muted/50 transition-colors",
                              selectedOrg?.organization_id === org.organization_id && "bg-blue-50 border-l-2 border-l-blue-500"
                            )}
                          >
                            <p className={cn("font-medium text-sm", colors.textPrimary)}>{org.name}</p>
                            <p className={cn("text-xs", colors.textMuted)}>
                              {org.plan_tier} • {org.user_count || 0} users
                            </p>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                  
                  {selectedOrg && (
                    <div className={cn("p-2 rounded bg-blue-50 border border-blue-200 text-sm")}>
                      Selected: <strong>{selectedOrg.name}</strong>
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* User Details */}
            <div className="space-y-4 pt-2">
              <h3 className={cn("text-sm font-medium", colors.textPrimary)}>User Details</h3>
              
              <div className="space-y-2">
                <Label htmlFor="userName">Full Name *</Label>
                <Input
                  id="userName"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="John Smith"
                  data-testid="create-user-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="userEmail">Email Address *</Label>
                <Input
                  id="userEmail"
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="john@example.com"
                  data-testid="create-user-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Organization Role</Label>
                <Select value={orgRole} onValueChange={setOrgRole}>
                  <SelectTrigger data-testid="create-user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ORG_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        <div>
                          <span className="font-medium">{role.label}</span>
                          <span className={cn("ml-2 text-xs", colors.textMuted)}>{role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Authentication */}
            <div className="space-y-3 pt-2">
              <h3 className={cn("text-sm font-medium", colors.textPrimary)}>Authentication</h3>
              
              <div className={cn("border rounded-lg p-3 space-y-3", colors.border)}>
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="authGoogle"
                    name="auth"
                    checked={authMethod === 'google'}
                    onChange={() => setAuthMethod('google')}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="authGoogle" className="cursor-pointer">
                    <span className="font-medium">Google Sign-In</span>
                    <p className={cn("text-sm", colors.textMuted)}>User will sign in with Google</p>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="authPassword"
                    name="auth"
                    checked={authMethod === 'password'}
                    onChange={() => setAuthMethod('password')}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="authPassword" className="cursor-pointer">
                    <span className="font-medium">Password</span>
                    <p className={cn("text-sm", colors.textMuted)}>Set a password for email login</p>
                  </Label>
                </div>
                
                {authMethod === 'password' && (
                  <div className="ml-7 pt-2 space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Leave blank to auto-generate"
                            className="pr-10 font-mono"
                            data-testid="create-user-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className={cn("absolute right-2 top-1/2 -translate-y-1/2", colors.textMuted)}
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <Button variant="outline" size="icon" onClick={generatePassword} title="Generate password">
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className={cn("text-xs", colors.textMuted)}>
                        {password ? (password.length < 8 ? '⚠️ Must be at least 8 characters' : '✓ Password set') : 'A secure password will be generated if left blank'}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="requireChange"
                        checked={requirePasswordChange}
                        onCheckedChange={setRequirePasswordChange}
                      />
                      <Label htmlFor="requireChange" className={cn("text-sm cursor-pointer", colors.textMuted)}>
                        Require password change on first login
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Notifications */}
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="welcomeEmail"
                checked={sendWelcomeEmail}
                onCheckedChange={setSendWelcomeEmail}
              />
              <Label htmlFor="welcomeEmail" className={cn("text-sm cursor-pointer", colors.textMuted)}>
                Send welcome email with login credentials
              </Label>
            </div>
            
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={handleSubmit} 
                disabled={!isFormValid() || isSubmitting}
                data-testid="create-user-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create User'
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
        
        {/* Success View */}
        {result && (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-2">
              <p className="text-green-700 font-medium">✓ User: {result.user?.email}</p>
              <p className="text-green-700">✓ Organization: {result.team_member?.organization_name}</p>
              <p className="text-green-700">✓ Role: {result.team_member?.role}</p>
              {result.notifications_sent?.includes('welcome_email') && (
                <p className="text-green-700">✓ Welcome email sent</p>
              )}
            </div>
            
            <div className={cn("border rounded-lg p-4", colors.border)}>
              <div className="flex items-center justify-between mb-3">
                <h4 className={cn("font-medium", colors.textPrimary)}>Login Credentials</h4>
                <Button variant="outline" size="sm" onClick={copyAllCredentials}>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy All
                </Button>
              </div>
              
              <div className="space-y-2 font-mono text-sm">
                <div className="flex items-center justify-between">
                  <span className={colors.textMuted}>URL:</span>
                  <div className="flex items-center gap-2">
                    <span>{result.credentials?.login_url}</span>
                    <button onClick={() => copyToClipboard(result.credentials?.login_url, 'URL')}>
                      <Copy className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className={colors.textMuted}>Email:</span>
                  <div className="flex items-center gap-2">
                    <span>{result.credentials?.email}</span>
                    <button onClick={() => copyToClipboard(result.credentials?.email, 'Email')}>
                      <Copy className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                    </button>
                  </div>
                </div>
                
                {result.credentials?.password ? (
                  <div className="flex items-center justify-between">
                    <span className={colors.textMuted}>Password:</span>
                    <div className="flex items-center gap-2">
                      <span>{result.credentials.password}</span>
                      <button onClick={() => copyToClipboard(result.credentials.password, 'Password')}>
                        <Copy className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className={colors.textMuted}>Auth:</span>
                    <span>Sign in with Google</span>
                  </div>
                )}
              </div>
            </div>
            
            {result.errors?.length > 0 && (
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-yellow-700 font-medium text-sm">Warnings:</p>
                {result.errors.map((err, idx) => (
                  <p key={idx} className="text-yellow-600 text-sm">• {err}</p>
                ))}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => { resetForm(); }}>
                Create Another
              </Button>
              <Button onClick={handleClose}>Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserModal;
